import { Loader2, AlertTriangle } from 'lucide-react';
import { useDeleteChannel } from '@/hooks/useChannelConnections';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-handler';

interface DeleteChannelDialogProps {
  channelId: string | null;
  channelName?: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Simple delete channel dialog
 * TODO: Replace with shadcn/ui AlertDialog component
 */
export function DeleteChannelDialog({
  channelId,
  channelName,
  open,
  onClose,
  onSuccess,
}: DeleteChannelDialogProps) {
  const { toast } = useToast();
  const { mutate: deleteChannel, isLoading } = useDeleteChannel(channelId || '');

  const handleDelete = () => {
    if (!channelId) return;

    deleteChannel(undefined, {
      onSuccess: () => {
        toast({
          title: 'Canal deletado',
          description: `O canal "${channelName || 'sem nome'}" foi removido com sucesso.`,
        });
        onClose();
        onSuccess?.();
      },
      onError: (error) => {
        const message = getErrorMessage(error, 'Ocorreu um erro inesperado');
        toast({
          title: 'Erro ao deletar canal',
          description: message,
          variant: 'destructive',
        });
      },
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-full bg-red-100 p-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold">Deletar Canal</h2>
        </div>

        <p className="text-gray-600 mb-6">
          Tem certeza que deseja deletar o canal <strong>"{channelName || 'sem nome'}"</strong>?
          <br />
          <br />
          Esta ação não pode ser desfeita. Todas as configurações e credenciais serão removidas
          permanentemente.
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleDelete}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isLoading ? 'Deletando...' : 'Deletar'}
          </button>
        </div>
      </div>
    </div>
  );
}
